import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { endOfDay, startOfDay } from 'date-fns';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
} from '@virtual-care/shadcn';
import {
  DateField,
  FieldMessage,
  FormMessage,
  TheButton,
  client,
  errorToFormIssue,
} from '@virtual-care/ui';

import {
  promoDateRangeError,
  promoDateRangeFields,
  refinePromoDateRange,
} from './schema.ts';

const schema = z
  .object({
    discountPercent: z.coerce.number().int().min(1).max(100),
    maxUses: z.coerce.number().int().min(1),
    ...promoDateRangeFields,
  })
  .refine(refinePromoDateRange, promoDateRangeError);

type PromoCode = {
  id: string;
  code: string;
  discountPercent: number;
  maxUses: number;
  usedCount: number;
  validFrom: string;
  validUntil: string;
};

export function UpdatePromoCodeDialog({
  promoCode,
  trigger,
  open,
  onOpenChange,
}: {
  promoCode: PromoCode;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const form = useForm({
    defaultValues: {
      discountPercent: promoCode.discountPercent,
      maxUses: promoCode.maxUses,
      validFrom: promoCode.validFrom,
      validUntil: promoCode.validUntil,
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await client.request('PATCH /promo-codes/{id}', {
            id: promoCode.id,
            discountPercent: value.discountPercent,
            maxUses: value.maxUses,
            validFrom: startOfDay(new Date(value.validFrom)).toISOString(),
            validUntil: endOfDay(new Date(value.validUntil)).toISOString(),
          });
          queryClient.invalidateQueries({ queryKey: ['promo-codes'] });
          onOpenChange(false);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Promo Code — {promoCode.code}</DialogTitle>
          <DialogDescription>
            Used {promoCode.usedCount} of {promoCode.maxUses}. maxUses cannot
            be lowered below usedCount.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.Field
            name="discountPercent"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="discountPercent">Discount percent</Label>
                <Input
                  id="discountPercent"
                  type="number"
                  min={1}
                  max={100}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(Number(e.target.value))}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="maxUses"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="maxUses">Max uses</Label>
                <Input
                  id="maxUses"
                  type="number"
                  min={promoCode.usedCount || 1}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(Number(e.target.value))}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="validFrom"
            children={(field) => (
              <DateField
                field={field}
                label="Valid from"
                placeholder="Pick a start date"
                onBlur={field.handleBlur}
              />
            )}
          />
          <form.Field
            name="validUntil"
            children={(field) => (
              <DateField
                field={field}
                label="Valid until"
                placeholder="Pick an end date"
                onBlur={field.handleBlur}
              />
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <form.Subscribe
              selector={(state) =>
                [state.canSubmit, state.isSubmitting] as const
              }
              children={([canSubmit, isSubmitting]) => (
                <TheButton
                  type="submit"
                  loading={isSubmitting}
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </TheButton>
              )}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
